import { autoinject, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/full-screen';
import groupedCategories from 'highcharts-grouped-categories';
import { Models } from 'models/core';
import { NetsServiceDashboardService } from 'services/statistics-dashboard-service';
import { AsyncErrorHandler } from 'lib/ui';
import { QueryFilter } from 'lib/tables/DataFilter';
import { PubSub } from 'lib/event';
import { NetsDamagedByCustomerDTO, NetsDamagedBySiteDTO, NetsServiceDashboard, ServiceStatusYearlyStringCountDto } from 'models/statistics/nets';
import { assertNever } from 'lib/helpers/assertion';
import { HighchartChartKind } from 'lib/highchart/types';

// Provides grouped categories functionality to Highcharts.
// eslint-disable-next-line @typescript-eslint/no-unsafe-call
groupedCategories(Highcharts);

export interface FieldMap {
  name: string;
  points: string;
  x?: string;
  y?: string;
}

type ChartView =
  | 'site-1-3'
  | 'site-4-10'
  | 'site-10+'
  | 'customer-1-3'
  | 'customer-4-10'
  | 'customer-10+'
  | 'nets-service'
  | 'special-products'
  | 'customer-top-with-most-damages';

@autoinject()
export class NetsServiceWidget {
  private container!: HTMLDivElement;
  private chart?: Highcharts.Chart;

  protected fetchingData = false;

  private repairType: Models.RepairType = Models.RepairType.Repairs1To3Msk;
  private isCustomer = false;

  // This is data for charts.
  private netsDamageDataPerSite: NetsDamagedBySiteDTO[];
  private netsDamageDataPerCustomer: NetsDamagedByCustomerDTO[];
  protected hasData = false;

  private nTopCustomers: number = 10;

  private chartType: HighchartChartKind = 'column';

  @bindable() allData: NetsServiceDashboard;
  allDataChanged() {
    this.render();
  }

  @bindable queryFilter: QueryFilter;
  async queryFilterChanged() {
    await this.fetchNetsDamageData();
    void this.render();
  }

  chartOptionsButtons: {
    label: string;
    isActive: boolean;
    id: ChartView;
  }[] = [];

  private activeChartView: ChartView = 'nets-service';

  constructor(private t: I18N, private netsServiceDashboardService: NetsServiceDashboardService, private pubsub: PubSub) {
    pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());
    this.createChartButtons();
  }

  private onLocaleChanged() {
    this.createChartButtons();
    void this.render();
  }

  @AsyncErrorHandler
  async bind() {
    await this.selectChart('nets-service');
  }

  detached() {
    this.destroyChart();
    this.pubsub.unsub();
  }

  private createChartButtons() {
    const siteId = this.t.tr('general.SiteId');
    const customer = this.t.tr('net.customer');
    const netsPlural = this.t.tr('general.nets');
    const quarter = this.t.tr('general.quarter');
    const specialProducts = this.t.tr('general.specialproducts');
    const serviceCardRepairPlacement = this.t.tr('reports.service.servicecard.repairplacement');
    const customersWithMostDamages = this.t.tr('customer.withMostDamage');

    this.chartOptionsButtons = [
      { label: `${serviceCardRepairPlacement} (1-3msk) Per ${siteId}`, isActive: false, id: 'site-1-3' },
      { label: `${serviceCardRepairPlacement} (4-10msk) Per ${siteId}`, isActive: false, id: 'site-4-10' },
      { label: `${serviceCardRepairPlacement} (>10msk) Per ${siteId}`, isActive: false, id: 'site-10+' },
      { label: `${serviceCardRepairPlacement} (1-3msk) Per ${customer}`, isActive: false, id: 'customer-1-3' },
      { label: `${serviceCardRepairPlacement} (4-10msk) Per ${customer}`, isActive: false, id: 'customer-4-10' },
      { label: `${serviceCardRepairPlacement} (>10msk) Per ${customer}`, isActive: false, id: 'customer-10+' },
      { label: `${netsPlural} Per ${quarter}`, isActive: true, id: 'nets-service' },
      { label: `${specialProducts} Per ${quarter}`, isActive: true, id: 'special-products' },
      { label: `${customersWithMostDamages}`, isActive: true, id: 'customer-top-with-most-damages' },
    ];
  }

  @AsyncErrorHandler
  async fetchNetsDamageData() {
    if (!this.queryFilter) return;

    this.fetchingData = true;

    let fetchTopNRecords = false;

    if (this.isCustomer == false) {
      const { data } = await this.netsServiceDashboardService.getNetsDamageDataPerSite(this.queryFilter, false, this.repairType);
      this.netsDamageDataPerSite = data?.YearlyNetsDamageReportBySite ?? [];
    } else {
      if (this.activeChartView == 'customer-top-with-most-damages') {
        fetchTopNRecords = true;
        this.queryFilter.top = this.nTopCustomers;
        this.queryFilter.asQueryString = this.queryFilter.asQueryString.replace(/([?&]top=)(\d+)/, `$1${this.queryFilter.top}`);
      }
      const { data } = await this.netsServiceDashboardService.getNetsDamageDataPerCustomer(
        this.queryFilter,
        false,
        this.repairType,
        fetchTopNRecords
      );
      this.netsDamageDataPerCustomer = data?.YearlyNetsDamageReportByCustomer ?? [];
    }

    this.fetchingData = false;
  }

  protected async selectChart(view: ChartView) {
    if (!view) return;
    this.activeChartView = view;

    this.isCustomer = view.startsWith('customer');

    switch (view) {
      case 'site-1-3':
      case 'customer-1-3':
        this.repairType = Models.RepairType.Repairs1To3Msk;
        break;
      case 'site-4-10':
      case 'customer-4-10':
        this.repairType = Models.RepairType.Repairs4To10Msk;
        break;
      case 'site-10+':
      case 'customer-10+':
        this.repairType = Models.RepairType.RepairsOver10Msk;
        break;
      case 'customer-top-with-most-damages':
        this.repairType = Models.RepairType.Unknown;
        break;
      case 'nets-service':
      case 'special-products':
        this.repairType = Models.RepairType.Unknown;
        break;
      default:
        assertNever(view);
    }

    await this.fetchNetsDamageData();
    void this.render();
  }

  private getChartOptions() {
    switch (this.activeChartView) {
      case 'site-1-3':
      case 'site-4-10':
      case 'site-10+':
      case 'customer-1-3':
      case 'customer-4-10':
      case 'customer-10+':
      case 'customer-top-with-most-damages':
        return this.createDamageChartOptions();
      case 'nets-service':
      case 'special-products':
        return this.createServiceChartOptions();
      default:
        assertNever(this.activeChartView);
    }
  }

  renderTimer = 0;
  private render() {
    this.destroyChart();
    window.clearTimeout(this.renderTimer);
    this.hasData = this.netsDamageDataPerSite?.length > 0 || this.netsDamageDataPerCustomer?.length > 0;

    if (!this.hasData || !this.container) return;
    // Put in timer to avoid multiple renders in short time if data changes quickly.
    this.renderTimer = window.setTimeout(() => {
      this.chart = Highcharts.chart(this.container, this.getChartOptions());
    }, 500);
  }

  private createDamageChartOptions(): Highcharts.Options {
    const BUTTON_HEIGHT = 30;
    const BUTTON_SPACING = 10;

    let data: { label: string; entries: { Year: number; DamageCount: number }[] }[] = [];

    switch (this.activeChartView) {
      case 'site-1-3':
      case 'site-4-10':
      case 'site-10+': {
        const _data = this.netsDamageDataPerSite || [];
        data = _data.map((siteDamage) => ({
          label: siteDamage.SiteName || '',
          entries: siteDamage.YearlyDamage || [],
        }));
        break;
      }
      case 'customer-1-3':
      case 'customer-4-10':
      case 'customer-10+':
      case 'customer-top-with-most-damages': {
        const _data = this.netsDamageDataPerCustomer || [];
        data = _data.map((custDamage) => ({
          label: custDamage.CustomerName || '',
          entries: custDamage.YearlyDamage || [],
        }));
        break;
      }
      default:
        return {};
    }

    const years = Array.from(new Set(data.flatMap((item) => item.entries.map((p) => +p.Year)))).sort();

    const series = data.map((item) => {
      const values = years.map((y) => {
        return item.entries.find((p) => p.Year === y)?.DamageCount ?? null;
      });
      return { type: this.chartType, name: item.label ?? '', data: values } satisfies Highcharts.SeriesOptionsType;
    });

    const tooltip_x = this.t.tr('general.year');
    const tooltip_y = `${this.t.tr('general.count')} (${this.t.tr('general.average')})`;

    return {
      chart: { type: this.chartType },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: { categories: years.map(String), title: { text: tooltip_x } },
      yAxis: { title: { text: tooltip_y }, allowDecimals: false },
      tooltip: {
        shared: false,
        formatter: function () {
          const year = this.category ?? this.x;
          if (this.y == null) return false;
          return `<b>${this.series?.name}</b><br/>${tooltip_x}: ${year}<br/>${tooltip_y}: ${this.y}`;
        },
      },
      plotOptions: { series: { connectNulls: true } },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemDistance: 30,
        itemWidth: 200,
        itemStyle: { width: 150, whiteSpace: 'normal' },
        maxHeight: 120,
      },
      series,
      navigation: {
        buttonOptions: {
          align: 'right',
          verticalAlign: 'top',
          buttonSpacing: BUTTON_SPACING,
          height: BUTTON_HEIGHT,
          y: 0,
        },
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF'],
          },
          toggleAll: {
            text: 'Deselect all',
            align: 'right',
            verticalAlign: 'top',
            titleKey: 'toggleSeries',
            onclick: function (this: Highcharts.Chart) {
              // Treat undefined `visible` as true
              const allVisible = this.series.every((s) => s.visible !== false);
              this.series.forEach((s) => s.setVisible(!allVisible, false));
              this.redraw();
              const newLabel = allVisible ? 'Select all' : 'Deselect all';
              this.update({ exporting: { buttons: { toggleAll: { text: newLabel } } } }, false);
              this.redraw();
            },
          },
        },
      },
    };
  }

  createServiceChartOptions(): Highcharts.Options {
    let data: ServiceStatusYearlyStringCountDto[] = [];

    switch (this.activeChartView) {
      case 'nets-service':
        data = this.allData?.YearlyNetsServiceData || [];
        break;
      case 'special-products':
        data = this.allData?.YearlySpecialProductsServiceData || [];
        break;
      default:
        return {};
    }

    const BUTTON_HEIGHT = 30;
    const BUTTON_SPACING = 10;
    const TOP_CLEARANCE = BUTTON_HEIGHT + BUTTON_SPACING + 8;

    // --- chart for quarterly data ---
    const yearToQuarters = new Map<number, Set<string>>();

    for (const item of data) {
      const counts = item.ServiceStatusCounts ?? [];

      for (const entry of counts) {
        const year = entry.Year;
        const querterlyData = entry.QuarterlyData ?? [];
        querterlyData.forEach((qRec) => {
          let quarterSet = yearToQuarters.get(year);
          if (!quarterSet) {
            quarterSet = new Set();
            yearToQuarters.set(year, quarterSet);
          }
          quarterSet.add(qRec.Quarter);
        });
      }
    }

    const sortedYears = Array.from(yearToQuarters.keys()).sort((a, b) => a - b);
    const groupedCategories = sortedYears.map((year) => ({
      name: year.toString(),
      categories: Array.from(yearToQuarters.get(year)).sort((a, b) => a.localeCompare(b)),
    }));

    const leafOrder: { year: number; quarter: string }[] = groupedCategories.flatMap((g) =>
      g.categories.map((q) => ({ year: Number(g.name), quarter: q }))
    );

    const series = data.map((item) => {
      const years = item.ServiceStatusCounts ?? [];

      const values = leafOrder.map(({ quarter, year }) => {
        const records = years.find((y) => y.Year === year)?.QuarterlyData ?? [];
        return records.find((q) => q.Quarter === quarter)?.Count ?? null;
      });

      return { type: this.chartType, name: item.ServiceStatus, data: values } as Highcharts.SeriesOptionsType;
    });

    const tooltip_label = this.t.tr('general.year');
    const tooltip_y = this.t.tr('general.count');

    const options: Highcharts.Options = {
      chart: {
        type: this.chartType,
        marginTop: TOP_CLEARANCE,
        scrollablePlotArea: {
          minWidth: leafOrder.length * 60,
          scrollPositionX: 0.5,
        },
        events: {
          render() {
            this.xAxis.forEach(
              (
                ax: Highcharts.Axis & {
                  axisGroup?: {
                    translateX: number;
                  };
                  axisTitle?: {
                    translateX: number;
                  };
                }
              ) => {
                if (ax.axisGroup) {
                  ax.axisGroup.translateX = 0;
                }
                if (ax.axisTitle) {
                  ax.axisTitle.translateX = 0;
                }
              }
            );
          },
        },
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        categories: groupedCategories as any,
        title: {
          text: tooltip_label,
          margin: 5,
          align: 'middle',
          style: {},
        },
        labels: {
          groupedOptions: [
            {
              style: { fontWeight: 'bold', fontSize: '25px' },
            },
            {
              rotation: 0,
              style: { fontSize: '12px' },
            },
          ],
        } as Highcharts.XAxisOptions & { groupedOptions: object[] },
        lineWidth: 1,
        lineColor: '#333',
        offset: 0,
        startOnTick: true,
        endOnTick: true,
      },
      yAxis: {
        title: { text: tooltip_y },
        allowDecimals: false,
      },
      tooltip: {
        shared: false,
        formatter() {
          const cat = this.category;
          if (this.y == null) return false;
          return `<b>${this.series?.name}</b><br/>${tooltip_label}: ${cat}<br/>${tooltip_y}: ${this.y}`;
        },
      },
      plotOptions: {
        series: { connectNulls: true },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemDistance: 20,
        itemWidth: 100,
        itemStyle: { width: 150, whiteSpace: 'normal' },
        maxHeight: 120,
      },
      series,
      navigation: {
        buttonOptions: {
          align: 'right',
          verticalAlign: 'top',
          buttonSpacing: BUTTON_SPACING,
          height: BUTTON_HEIGHT,
          y: 0,
        },
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF'],
          },
          toggleAll: {
            text: 'Deselect all',
            align: 'right',
            verticalAlign: 'top',
            titleKey: 'toggleSeries',
            onclick: function (this: Highcharts.Chart) {
              // Treat undefined `visible` as true
              const allVisible = this.series.every((s) => s.visible !== false);
              this.series.forEach((s) => s.setVisible(!allVisible, false));
              this.redraw();
              const newLabel = allVisible ? 'Select all' : 'Deselect all';
              this.update({ exporting: { buttons: { toggleAll: { text: newLabel } } } }, false);
              this.redraw();
            },
          },
        },
      },
    };

    return options;
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }
}
